import { useReveal } from '../lib/hooks'

/**
 * A full-width band of content.
 *
 * @param {object} props
 * @param {'white'|'paper'|'dark'} [props.tone]
 * @param {boolean} [props.tight]     Reduced vertical rhythm.
 * @param {boolean} [props.hairline]  Top border, for two adjacent light bands.
 * @param {'default'|'wide'|'prose'} [props.width]
 * @param {string} [props.id]         Anchor target.
 */
export function Section({
  children,
  tone = 'white',
  tight = false,
  hairline = false,
  width = 'default',
  id,
  className = '',
  ...rest
}) {
  const classes = [
    'section',
    tone !== 'white' ? `section--${tone}` : '',
    tight ? 'section--tight' : '',
    hairline ? 'section--hairline' : '',
    tone === 'dark' ? 'on-dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const container = [
    'container',
    width !== 'default' ? `container--${width}` : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={classes} id={id} {...rest}>
      <div className={container}>{children}</div>
    </section>
  )
}

/**
 * Eyebrow + heading + optional lead paragraph, revealed on scroll.
 *
 * @param {object} props
 * @param {string} [props.eyebrow]
 * @param {string} props.title
 * @param {string} [props.lead]
 * @param {boolean} [props.center]
 * @param {1|2|3} [props.level]  Heading level; defaults to h2.
 */
export function SectionHead({
  eyebrow,
  title,
  lead,
  center = false,
  level = 2,
  children,
}) {
  const [ref, visible] = useReveal()
  const Heading = `h${level}`

  return (
    <div
      ref={ref}
      data-visible={visible}
      className={`section-head reveal${center ? ' section-head--center' : ''}`}
    >
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      <Heading>{title}</Heading>
      {lead ? <p>{lead}</p> : null}
      {children}
    </div>
  )
}
